import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { materializeFile } from '../../photo-materialize';

/**
 * 10 MiB — mirrors backend `MAX_PHOTO_BYTES` at
 * `src/book/photos/photos.constants.ts:65`. Client-side check is
 * additive: the backend enforces the same cap at GCS PUT time (413),
 * so bypassing the client check still fails at the server; the
 * client check exists so the customer sees a real message inline at
 * the slot instead of the silent-generic error class
 * `photoUploadOverlayError` at the LightGate boundary
 * (Befund at upload path, Sebastian ruling
 * 2026-08-12).
 */
const CLIENT_MAX_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * MIME types the client accepts before handing the File to
 * `usePhotoUpload`. Superset of `ALLOWED_PHOTO_CONTENT_TYPES` at the
 * backend (jpeg / png / webp / heic / heif) — the sanitizer converts
 * heic + heif to JPEG server-side via heic-convert. `image/heic-
 * sequence` and `image/heif-sequence` cover the multi-image HEIC
 * variants iOS occasionally emits.
 *
 * Empty `file.type` is ALLOWED: some iOS versions leave the MIME
 * blank on a HEIC pick from the Files app, and rejecting on empty
 * type would trap the very customer the HEIC path exists to serve.
 * The magic-byte detection at the sanitizer covers those uploads.
 */
const CLIENT_ACCEPT_MIMES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

/**
 * v3.1 §6 Schritt 1 line 259 · Foto-Upload-Slot with foto-rules.
 *
 * Bau-Spez v3.1 §6 line 259 verbatim: "darunter Upload-Slot mit den
 * Foto-Regeln direkt am Slot".
 *
 * Foto-rules verbatim source: Textdatei v2 lines 170-172. Four lines,
 * displayed under the slot when `showRules` is true (the mandatory
 * face slot). The optional Ganzkörper slot re-uses the same primitive
 * with `showRules=false` — the rules only need to appear once.
 *
 * ═══ TWO STATES (Sebastian ruling 2026-08-06 · Option 3) ═══════════
 *
 * The signed-upload endpoint is `@UseGuards(JwtAuthGuard)` and the
 * customer is unauthenticated during Schritt 1, so no upload happens
 * per file pick — File objects live in transient React state until
 * the Light-Gate registers the customer and Commit 3 fires the bulk
 * upload with its own progress UI. The slot therefore shows:
 *
 *   `empty`   — dashed border, "+ Foto hinzufügen", clickable full
 *               slot to open the OS file picker. Foto-Regeln appear
 *               below when `showRules=true`.
 *   `picked`  — square preview (blob URL from URL.createObjectURL) +
 *               Entfernen button. Clicking the preview does NOT
 *               re-open the picker; the parent requires an explicit
 *               Entfernen → re-pick path so a fat-thumb mispick does
 *               not silently swap the File.
 *
 * Tracker `[book-photos-no-preauth-upload]` at
 * `context/dreamable_open_items_checklist.md` records why the third
 * `uploading` state does not live here — it belongs inside the
 * Light-Gate overlay in Commit 3, where the bulk upload runs.
 *
 * ═══ ROLE SEPARATION (Bau-Spez §A3.2) ═══════════════════════════════
 *
 * §A3.2 defines face vs full-body as non-interchangeable roles. The
 * caller sets `ariaLabel` to distinguish the two — but the visual is
 * the same primitive to keep the two blocks consistent.
 *
 * ═══ FILE INPUT ═════════════════════════════════════════════════════
 *
 * Hidden `<input type="file" accept="image/*">`. The visible slot is a
 * `<button>` that triggers the input via ref. On file pick, fires
 * `onFileSelected(file)`; the parent creates the preview URL via
 * `URL.createObjectURL(file)` and passes it back as `previewUrl` in
 * the `picked` state.
 */

/**
 * Sebastian ruling 2026-09-23 — HEIC-aware slot. Four states cover
 * the picker outcomes:
 *
 *   `empty`      — slot is unpicked.
 *   `picked`     — non-HEIC image (jpeg/png/webp) OR a HEIC that was
 *                  successfully decoded in the browser (Safari
 *                  native, or the libheif-js Web Worker). Carries a
 *                  preview blob URL.
 *   `converting` — HEIC pick, browser decode in progress. Slot shows
 *                  a small spinner + "Vorschau wird erstellt …".
 *                  Photo counts as picked (submission-wise); the
 *                  Entfernen button stays live so mid-decode
 *                  cancellation works.
 *   `heic`       — HEIC pick that the browser could not decode
 *                  (native decode threw + libheif worker threw or
 *                  timed out or file too large). Slot shows a
 *                  neutral photo icon + the German confirmation
 *                  "Foto ausgewählt. iPhone-Fotos kannst du erst
 *                  nach dem Hochladen sehen.". Photo STILL counts
 *                  as picked so submission proceeds — the ORIGINAL
 *                  File goes to GCS and the backend sanitizer does
 *                  the authoritative HEIC → JPEG conversion.
 */
export type FotoUploadSlotState =
  | { readonly kind: 'empty' }
  | { readonly kind: 'converting' }
  | { readonly kind: 'picked'; readonly previewUrl: string }
  | { readonly kind: 'heic' };

/**
 * Local inline validation error state — client-side pre-upload check
 * only. `null` = no error to render. Three kinds, each interpolating
 * `castName` into its STRINGS body:
 *   - `oversize`     — file.size > 10 MiB
 *   - `wrongFormat`  — file.type not in CLIENT_ACCEPT_MIMES
 *   - `readFailed`   — materialize `arrayBuffer()` rejected or
 *                      resolved to zero bytes (Sebastian ruling
 *                      2026-10-28; distinct from wrongFormat because
 *                      the MIME is fine, the bytes are not readable).
 */
type ClientValidationError =
  | { readonly kind: 'oversize' }
  | { readonly kind: 'wrongFormat' }
  | { readonly kind: 'readFailed' }
  | null;

function validationErrorTemplate(err: NonNullable<ClientValidationError>): string {
  switch (err.kind) {
    case 'oversize':
      return STRINGS.book.personen.uploadOversize;
    case 'wrongFormat':
      return STRINGS.book.personen.uploadWrongFormat;
    case 'readFailed':
      return STRINGS.book.personen.uploadReadFailed;
  }
}

export interface FotoUploadSlotProps {
  readonly state: FotoUploadSlotState;
  readonly onFileSelected: (file: File) => void;
  readonly onRemove?: () => void;
  /** True on the mandatory face slot; false on the optional full-body. */
  readonly showRules: boolean;
  /** ARIA role/description — differentiates face vs full-body slot. */
  readonly ariaLabel?: string;
  readonly testId?: string;
  /**
   * Cast member name for client-side error interpolation
   * (`STRINGS.book.personen.uploadOversize` / `uploadWrongFormat`).
   * Optional so callers that don't have a name in scope (e.g. a
   * generic upload slot outside the cast context) render without the
   * name — falls back to empty string in the `{castName}` slot.
   */
  readonly castName?: string;
  /**
   * Sebastian ruling 2026-09-18 — the K2 confirmation dialog fires
   * right after upload, and its "Anderes Foto wählen" button drops
   * the current photo and reopens the OS file picker programmatically.
   * `slotPickedDetailHint` renders next to the small thumbnail in the
   * `picked` state — omit it on non-cast surfaces (Ganzkörper, etc.)
   * via `showDetailHint=false`.
   */
  readonly showDetailHint?: boolean;
  /**
   * Sebastian ruling 2026-09-19 — when set, the slot renders (a) a
   * small caption above the empty state naming the slot as optional +
   * what it is good for, and (b) a muted empty-state visual (thin
   * solid border, smaller padding, no big "+"). Consumed today by the
   * full-body Ganzkörper slot in `PersonenRoute.tsx`; face slot omits
   * the prop so the first photo keeps its prominent affordance.
   */
  readonly optionalCaption?: string;
  /**
   * Sebastian ruling 2026-09-24 (round 2) — suppress the internal
   * "Bis 10 MB. Fotos direkt vom Handy funktionieren." line when the
   * parent renders its own copy of that sentence in a specific
   * position. Defaults to true to preserve the pre-existing
   * FotoUploadSlot behaviour on every other consumer (LightGate,
   * Ganzkörper, photo-swap). The PersonenFlow Q_photo layout sets
   * this to false so the sentence appears exactly ONCE, below
   * `qPhotoBelow` per Sebastian's positioning spec.
   */
  readonly showLimitsLine?: boolean;
}

/**
 * Sebastian ruling 2026-09-18 — imperative handle exposed via
 * `React.forwardRef` so a parent (e.g. `PersonenRoute.tsx`) can
 * trigger the OS file picker on a specific slot without a click on
 * the visible slot button. Consumed by the "Anderes Foto wählen"
 * flow in the K2 confirmation dialog: parent drops the photo, then
 * calls `.pick()` on the same slot's handle to reopen the picker in
 * one motion.
 */
export interface FotoUploadSlotHandle {
  readonly pick: () => void;
}

export const FotoUploadSlot = forwardRef<FotoUploadSlotHandle, FotoUploadSlotProps>(
  function FotoUploadSlot(
    {
      state,
      onFileSelected,
      onRemove,
      showRules,
      ariaLabel,
      testId,
      castName,
      showDetailHint = true,
      optionalCaption,
      showLimitsLine = true,
    },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [validationError, setValidationError] = useState<ClientValidationError>(null);
    useImperativeHandle(
      ref,
      () => ({
        pick: () => inputRef.current?.click(),
      }),
      [],
    );

    const handlePickClick = () => {
      if (state.kind !== 'empty') return;
      inputRef.current?.click();
    };

    const isPicked =
      state.kind === 'picked' || state.kind === 'converting' || state.kind === 'heic';

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset the input value so re-picking the same file after a remove
      // still fires `change`.
      event.target.value = '';
      if (file === undefined) return;

      // Client-side pre-upload validation — Sebastian ruling 2026-08-12,
      // Befund at the upload path. Runs BEFORE the parent's
      // `onFileSelected` so a rejected pick never becomes local pick
      // state (no preview URL leak, no confused "picked but errored"
      // UI). Order: size FIRST so the customer sees the actionable
      // 10 MB rule before the format rule.
      if (file.size > CLIENT_MAX_PHOTO_BYTES) {
        setValidationError({ kind: 'oversize' });
        return;
      }
      if (file.type !== '' && !CLIENT_ACCEPT_MIMES.has(file.type)) {
        setValidationError({ kind: 'wrongFormat' });
        return;
      }
      // Empty file.type (rare, iOS Files-app HEIC edge) is allowed —
      // magic-byte detection at the sanitizer handles it.

      // Materialize the picked File into an owned Blob-backed File.
      // On Android Chrome, `<input type="file">` can return a lazy
      // reference into a content URI (Google Photos / Drive / cloud
      // picker) whose bytes become unreadable minutes later. Reading
      // them NOW severs that dependency so every downstream consumer
      // (IDB persist, preview, upload) sees an owned, resident copy.
      // On failure, surface the Sebastian-authored readFailed inline
      // error — distinct from wrongFormat because the file's MIME is
      // fine, we just could not read its bytes.
      let materialized: File;
      try {
        materialized = await materializeFile(file, 'pick');
      } catch {
        setValidationError({ kind: 'readFailed' });
        return;
      }
      setValidationError(null);
      onFileSelected(materialized);
    };

    const slotBase =
      'flex w-full flex-col items-center justify-center rounded-lg text-center text-dream-navy transition-colors';

    return (
      <div data-testid={testId} className="space-y-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="sr-only"
          aria-hidden
          tabIndex={-1}
        />

        {state.kind === 'empty' && optionalCaption !== undefined && (
          <p className="text-xs text-dream-navy/70">{optionalCaption}</p>
        )}

        {state.kind === 'empty' && (
          <button
            type="button"
            onClick={handlePickClick}
            aria-label={ariaLabel ?? STRINGS.book.personen.slotAriaEmpty}
            className={
              optionalCaption === undefined
                ? // Prominent primary affordance — the mandatory face
                  // slot. Big dashed rectangle, big "+", full-width.
                  `${slotBase} border-2 border-dashed border-dream-navy/30 bg-dream-white p-6 hover:bg-dream-background focus-visible:outline-2 focus-visible:outline-dream-navy/50`
                : // Sebastian ruling 2026-09-19 — muted optional slot.
                  // Thin solid border, smaller padding, no big icon.
                  // Reads as available-but-not-required so it stops
                  // competing with a filled face slot for the eye.
                  `${slotBase} border border-dream-navy/15 bg-dream-white p-3 hover:bg-dream-background focus-visible:outline-2 focus-visible:outline-dream-navy/50`
            }
          >
            {optionalCaption === undefined && (
              <span className="text-2xl" aria-hidden>
                +
              </span>
            )}
            <span
              className={
                optionalCaption === undefined
                  ? 'mt-1 text-sm font-medium'
                  : 'text-sm text-dream-navy/70'
              }
            >
              {STRINGS.book.personen.slotEmptyLabel}
            </span>
          </button>
        )}

        {isPicked && (
          <div aria-label={STRINGS.book.personen.slotAriaPicked} className="flex items-start gap-3">
            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-dream-navy/10">
              {state.kind === 'picked' && (
                <img src={state.previewUrl} alt="" className="h-full w-full object-cover" />
              )}
              {state.kind === 'converting' && (
                // Sebastian ruling 2026-09-23 — libheif-js worker
                // decode in progress. Same 96×96 thumbnail frame so
                // the layout does not jump when the preview lands.
                // Tailwind animate-spin primitive; navy on the same
                // light background as the empty thumbnail so it
                // reads calm, not alarming. 5 s hard timeout in
                // `previewHeicInBrowser`; on timeout the slot flips
                // to the `heic` state below.
                <div
                  role="status"
                  aria-live="polite"
                  aria-label={STRINGS.book.personen.slotPreviewConverting}
                  className="flex h-full w-full items-center justify-center"
                >
                  <span
                    aria-hidden
                    className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-dream-navy/30 border-t-dream-navy"
                  />
                </div>
              )}
              {state.kind === 'heic' && (
                // Sebastian ruling 2026-09-23 — HEIC pick renders a
                // neutral photo icon (NOT an error styling — Sebastian
                // wording: "calm confirmation rather than an error").
                // Muted navy on the same background as the empty
                // thumbnail so the slot reads filled + at-rest, not
                // broken. The German confirmation copy below carries
                // the "kannst du erst nach dem Hochladen sehen" line.
                <div
                  aria-hidden
                  className="flex h-full w-full items-center justify-center text-2xl text-dream-navy/40"
                >
                  📷
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              {state.kind === 'picked' && showDetailHint && (
                <p className="text-xs text-dream-navy/60">
                  {STRINGS.book.personen.slotPickedDetailHint}
                </p>
              )}
              {state.kind === 'converting' && (
                <p className="text-xs text-dream-navy/60">
                  {STRINGS.book.personen.slotPreviewConverting}
                </p>
              )}
              {state.kind === 'heic' && (
                <p className="text-xs text-dream-navy/60">{STRINGS.book.personen.slotHeicPicked}</p>
              )}
              {onRemove !== undefined && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="self-start text-sm text-dream-navy underline focus-visible:outline-2 focus-visible:outline-dream-navy/50"
                >
                  {STRINGS.book.personen.slotPickedRemoveLabel}
                </button>
              )}
            </div>
          </div>
        )}

        {showRules && state.kind === 'empty' && (
          <ul className="mt-2 space-y-0.5 text-xs text-dream-navy/70">
            <li>· {STRINGS.book.personen.fotoRules.onePerImage}</li>
            <li>· {STRINGS.book.personen.fotoRules.faceVisible}</li>
            <li>· {STRINGS.book.personen.fotoRules.noSunglassesCap}</li>
            <li>· {STRINGS.book.personen.fotoRules.everydayBetterThanStudio}</li>
          </ul>
        )}

        {/*
        Limits line — WIDER gate than the four Foto-Regeln above:
        appears on EVERY empty slot (face + Ganzkörper + reupload
        after rejection), regardless of showRules. Sebastian ruling
        2026-08-12: "A customer must never pick a photo without
        seeing the limits under the field." Source: customer-text
        file "Limits am Upload-Slot".
      */}
        {state.kind === 'empty' && showLimitsLine && (
          <p className="mt-1 text-xs text-dream-navy/70">{STRINGS.book.personen.fotoLimitsLine}</p>
        )}

        {/*
        Inline validation error — replaces today's silent-generic
        error class at the LightGate upload boundary. Interpolates
        `{castName}` when the parent provided one; falls back to
        empty string otherwise (the sentence still parses).
      */}
        {validationError !== null && (
          <p className="mt-2 text-sm text-error" role="alert">
            {validationErrorTemplate(validationError).replace('{castName}', castName ?? '')}
          </p>
        )}
      </div>
    );
  },
);
